import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1350;

function GraphicPlayerSpotlightShowcase({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  canUseCutouts = false,
}) {
  const {
    highlightPlayer,
    highlightStats,
    homeTeam,
    backgroundPhoto,
    dominantImage,
    dominantImageX,
    dominantImageY,
    dominantImageScale,
    spotlightLabelFont,
    spotlightLabelSize,
    spotlightLabelColor,
    spotlightLabelX,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const scale = (dominantImageScale ?? 100) / 100;
  const heroFamily = "'Bebas Neue', 'Arial Narrow', Arial, sans-serif";
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const pillFont = spotlightLabelFont
    ? `'${spotlightLabelFont}', 'Arial Narrow', Arial, sans-serif`
    : barlowFamily;
  const pillSize = spotlightLabelSize ?? 56;
  const pillColor = spotlightLabelColor || '#1a1a1a';
  const pillX = spotlightLabelX ?? 540;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const statsLines = highlightStats ? highlightStats.split('\n').filter(Boolean) : [];

  return (
    <div
      id="graphic-player-spotlight-showcase"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFamily,
      }}
    >
      {/* Layer 1: Background photo */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 2: Dark overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 2,
        }}
      />

      {/* Layer 3: Dominant image */}
      {dominantImage && (
        <img
          src={dominantImage}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: canUseCutouts ? `${dominantImageY ?? 50}%` : 'auto',
            bottom: canUseCutouts ? 'auto' : 320,
            left: canUseCutouts ? `${dominantImageX ?? 50}%` : 540,
            transform: canUseCutouts
              ? `translate(-50%, -50%) scale(${scale})`
              : 'translateX(-50%) scale(1)',
            transformOrigin: 'center center',
            width: '100%',
            height: 'auto',
            maxWidth: 'none',
            overflow: 'visible',
            zIndex: 3,
          }}
        />
      )}

      {/* Layer 4: Bottom name banner */}
      <div
        style={{
          position: 'absolute',
          bottom: 160,
          left: 0,
          width: WIDTH,
          height: 280,
          background: teamColors.primary,
          zIndex: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            color: '#ffffff',
            fontFamily: heroFamily,
            fontWeight: 700,
            fontSize: 96,
            textTransform: 'uppercase',
            textAlign: 'center',
            lineHeight: 1,
            letterSpacing: 2,
            paddingLeft: 32,
            paddingRight: 32,
          }}
        >
          {highlightPlayer || 'PLAYER NAME'}
        </div>
        {homeTeam && (
          <div
            style={{
              color: '#ffffff',
              fontFamily: barlowFamily,
              fontSize: 40,
              opacity: 0.85,
              textAlign: 'center',
              marginTop: 12,
              letterSpacing: 3,
            }}
          >
            {homeTeam}
          </div>
        )}
      </div>

      {/* Layer 5: Stats strip */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: WIDTH,
          height: 160,
          background: 'rgba(0,0,0,0.75)',
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            color: '#ffffff',
            fontFamily: barlowFamily,
            fontSize: 36,
            textAlign: 'center',
            letterSpacing: 1,
            paddingLeft: 48,
            paddingRight: 48,
            lineHeight: 1.4,
          }}
        >
          {statsLines.length > 0 ? statsLines.join(' · ') : 'STATS & ACHIEVEMENTS'}
        </div>
      </div>

      {/* Layer 6a: Team logo (top-left) */}
      {homeTeamLogo && (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam || 'Team'}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 48,
            left: 48,
            height: 100,
            width: 'auto',
            zIndex: 6,
          }}
        />
      )}

      {/* Layer 6b: PLAYER SPOTLIGHT pill (centered top) */}
      <div
        style={{
          position: 'absolute',
          top: 48,
          left: pillX,
          transform: 'translateX(-50%)',
          background: '#ffffff',
          color: pillColor,
          fontFamily: pillFont,
          fontSize: pillSize,
          fontWeight: 700,
          letterSpacing: 3,
          borderRadius: 60,
          paddingTop: 16,
          paddingBottom: 16,
          paddingLeft: 48,
          paddingRight: 48,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          zIndex: 6,
        }}
      >
        PLAYER SPOTLIGHT
      </div>

      {/* Layer 7: Left accent bar */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 16,
          height: HEIGHT,
          background: teamColors.secondary,
          zIndex: 7,
        }}
      />

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicPlayerSpotlightShowcase;
